// src/private/src/pages/app/clients/components/ChannelMixChart.jsx
import React, { memo, useMemo } from 'react';
import { Box, Stack, Typography, Tooltip } from '@mui/material';

const CHANNEL_COLORS = {
  google: '#4285f4',
  facebook: '#1877f2',
  bing: '#00809d',
  tiktok: '#000000',
  default: '#9ca3af',
};

const getChannelColor = (name = '') => {
  const lower = name.toLowerCase().trim();
  return CHANNEL_COLORS[lower] || CHANNEL_COLORS.default;
};

/**
 * Horizontal stacked bar showing spend distribution across channels.
 * Each segment is proportional to spend share with a tooltip on hover.
 */
function ChannelMixChart({ channels = [], showFee = false }) {
  const data = useMemo(() => {
    const items = channels
      .map((ch) => {
        const name = ch.channel__name || ch.channel || 'Other';
        const spend = showFee
          ? (ch.spendWithFees || ch.total_spend_sum || 0)
          : (ch.total_spend_sum || 0);
        return { name, spend: Number(spend) || 0 };
      })
      .filter((d) => d.spend > 0)
      .sort((a, b) => b.spend - a.spend);

    const total = items.reduce((acc, d) => acc + d.spend, 0);
    return items.map((d) => ({
      ...d,
      pct: total > 0 ? (d.spend / total) * 100 : 0,
      color: getChannelColor(d.name),
    }));
  }, [channels, showFee]);

  const total = data.reduce((acc, d) => acc + d.spend, 0);

  if (!data.length) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
        <Typography variant="overline" color="text.secondary">
          Channel Mix
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ${total.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} total
        </Typography>
      </Stack>
      {/* Stacked bar */}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: 24,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {data.map((d) => (
          <Tooltip
            key={d.name}
            title={`${d.name}: $${d.spend.toLocaleString('en-US', { minimumFractionDigits: 0 })} (${d.pct.toFixed(1)}%)`}
            arrow
            placement="top"
          >
            <Box
              sx={{
                width: `${d.pct}%`,
                minWidth: d.pct > 0 ? 4 : 0,
                bgcolor: d.color,
                transition: 'width 300ms ease',
                cursor: 'default',
                '&:hover': { opacity: 0.8 },
              }}
            />
          </Tooltip>
        ))}
      </Box>
      {/* Legend */}
      <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
        {data.map((d) => (
          <Stack key={d.name} direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: d.color, flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {d.name} {d.pct.toFixed(0)}%
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

export default memo(ChannelMixChart);
